/**
 * DirectoryView — the "everyone in this tree" list.
 *
 * Surfaces persons the Family Tree canvas can't show:
 *   - disconnected persons (no parents, partners, or children)
 *   - persons unreachable from the current focal (different branch, sibling
 *     line, orphaned after a failed import)
 *   - orphan clusters — two or more persons who are connected to each other
 *     but not to the focal's main tree (often an imported sub-tree where a
 *     linking parent_child relationship is missing)
 *   - persons with incomplete data (no birth date, no name, no place)
 *
 * Click any row to open them in the side panel. Filter by status so a
 * researcher can work through "everything that needs a date" in one pass.
 */
import { useMemo, useState } from 'react'
import { useTreeStore } from '../../stores/tree.js'
import { useAuthStore } from '../../stores/auth.js'
import { extractYear } from '../../utils/date.js'
import BulkRequestModal from '../mysteries/BulkRequestModal.jsx'

// Decode the user id (sub) from the JWT — matches the owner-check pattern
// used in PersonPage / PersonPanel.
function getUserId(token) {
  try { return JSON.parse(atob(token.split('.')[1])).sub } catch { return null }
}

const FILTERS = [
  { id: 'all',              label: 'All'                 },
  { id: 'no_parents',       label: 'Missing parents'     },
  { id: 'disconnected',     label: 'No connections'      },
  { id: 'orphan_cluster',   label: 'Orphan cluster'      },
  { id: 'chain_gap',        label: 'Not in ancestor chain' },
  { id: 'no_birth',         label: 'Missing birth date'  },
  { id: 'no_place',         label: 'Missing birth place' },
  { id: 'unreachable',      label: 'Not in current tree' },
]

// Default research-request copy + gap type per gap filter, used to pre-fill
// the bulk "request research help" modal.
const FILTER_REQUEST_DEFAULTS = {
  no_parents:     { gap: 'vertical',   text: 'Help me find the parents of this person.' },
  disconnected:   { gap: 'horizontal', text: 'Help me connect this person to the rest of the tree.' },
  orphan_cluster: { gap: 'horizontal', text: 'Help me link this branch into the main tree.' },
  chain_gap:      { gap: 'vertical',   text: 'Help me find the missing link connecting this person up the ancestor chain.' },
  no_birth:       { gap: 'vertical',   text: 'Help me find the birth date for this person.' },
  no_place:       { gap: 'vertical',   text: 'Help me find the birth place for this person.' },
  unreachable:    { gap: 'horizontal', text: 'Help me connect this person to the rest of the tree.' },
}

export default function DirectoryView({ onPersonClick }) {
  const persons = useTreeStore(s => s.persons)
  const relationships = useTreeStore(s => s.relationships)
  const focalPersonId = useTreeStore(s => s.focalPersonId)
  const currentTree = useTreeStore(s => s.currentTree)
  const token = useAuthStore(s => s.token)
  const userId = getUserId(token)
  const isOwner = !!currentTree && !!userId && currentTree.owner_id === userId
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [bulkOpen, setBulkOpen] = useState(false)

  // Build reachability + connection maps once per data change.
  const {
    connectionCount,
    reachableFromFocal,
    clusterIdOf,       // Map<personId, clusterId>
    clusterSizeOf,     // Map<clusterId, size>
    mainClusterId,     // cluster containing the focal (or the largest one)
    chainReachable,    // Set of persons reachable from focal via parent_child only
    hasParent,         // Set of persons with at least one known parent
  } = useMemo(
    () => buildGraphIndex(persons, relationships, focalPersonId),
    [persons, relationships, focalPersonId]
  )

  const counts = useMemo(() => {
    const c = { all: persons.length, no_parents: 0, disconnected: 0, no_birth: 0, no_place: 0, unreachable: 0, orphan_cluster: 0, chain_gap: 0 }
    for (const p of persons) {
      if ((connectionCount.get(p.id) || 0) === 0) c.disconnected++
      if (!hasParent.has(p.id)) c.no_parents++
      if (!extractYear(p.birth_date)) c.no_birth++
      if (!p.birth_place) c.no_place++
      if (focalPersonId && !reachableFromFocal.has(p.id)) c.unreachable++
      // Orphan cluster = part of a multi-person component that isn't the main tree
      const cid = clusterIdOf.get(p.id)
      if (cid !== undefined && cid !== mainClusterId && (clusterSizeOf.get(cid) || 0) >= 2) {
        c.orphan_cluster++
      }
      // Chain gap = in main cluster (reachable via ANY edge from focal) but
      // NOT reachable via parent_child walk only. Means they're relatives we
      // know about but the parent_child chain skips them — likely the DB has
      // an ancestor row but no parent_child edge linking them upward.
      if (focalPersonId
          && p.id !== focalPersonId
          && reachableFromFocal.has(p.id)
          && !chainReachable.has(p.id)) {
        c.chain_gap++
      }
    }
    return c
  }, [persons, connectionCount, reachableFromFocal, focalPersonId, clusterIdOf, clusterSizeOf, mainClusterId, chainReachable, hasParent])

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    return persons
      .filter(p => {
        if (filter === 'no_parents' && hasParent.has(p.id)) return false
        if (filter === 'disconnected' && (connectionCount.get(p.id) || 0) !== 0) return false
        if (filter === 'no_birth' && extractYear(p.birth_date)) return false
        if (filter === 'no_place' && p.birth_place) return false
        if (filter === 'unreachable') {
          if (!focalPersonId) return false
          if (reachableFromFocal.has(p.id)) return false
        }
        if (filter === 'orphan_cluster') {
          const cid = clusterIdOf.get(p.id)
          if (cid === undefined) return false
          if (cid === mainClusterId) return false
          if ((clusterSizeOf.get(cid) || 0) < 2) return false
        }
        if (filter === 'chain_gap') {
          if (!focalPersonId) return false
          if (p.id === focalPersonId) return false
          if (!reachableFromFocal.has(p.id)) return false
          if (chainReachable.has(p.id)) return false
        }
        if (q) {
          const hay = [p.first_name, p.preferred_name, p.last_name, p.birth_place, p.birth_date]
            .filter(Boolean).join(' ').toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
      .sort((a, b) => {
        // Orphan cluster view: group by cluster (largest first), then by birth
        // year inside the cluster so the chain reads top-down.
        if (filter === 'orphan_cluster') {
          const ca = clusterIdOf.get(a.id)
          const cb = clusterIdOf.get(b.id)
          if (ca !== cb) {
            const sa = clusterSizeOf.get(ca) || 0
            const sb = clusterSizeOf.get(cb) || 0
            if (sa !== sb) return sb - sa
            return (ca ?? 0) - (cb ?? 0)
          }
          const ay = Number(extractYear(a.birth_date)) || 9999
          const by = Number(extractYear(b.birth_date)) || 9999
          if (ay !== by) return ay - by
        }
        // Chain gap view: oldest first (most likely the lost ancestor tip)
        if (filter === 'chain_gap') {
          const ay = Number(extractYear(a.birth_date)) || 9999
          const by = Number(extractYear(b.birth_date)) || 9999
          if (ay !== by) return ay - by
        }
        // Incomplete (no birth year) first when showing everything,
        // otherwise alphabetical by last name.
        const ay = Number(extractYear(a.birth_date)) || 0
        const by = Number(extractYear(b.birth_date)) || 0
        if (filter === 'all') {
          if (!!ay !== !!by) return !ay ? -1 : 1
        }
        const la = (a.last_name || '').toLowerCase()
        const lb = (b.last_name || '').toLowerCase()
        if (la !== lb) return la < lb ? -1 : 1
        const fa = (a.first_name || '').toLowerCase()
        const fb = (b.first_name || '').toLowerCase()
        return fa < fb ? -1 : 1
      })
  }, [persons, filter, query, connectionCount, reachableFromFocal, focalPersonId, clusterIdOf, clusterSizeOf, mainClusterId, chainReachable, hasParent])

  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <h2 style={{ fontFamily: 'Playfair Display, serif', margin: '0 0 0.25rem' }}>Everyone in this tree</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: '0 0 1.5rem' }}>
        Including people the Family Tree view can't show because they aren't yet connected
        to the current focal person, or are missing a birth date.
      </p>

      <input
        className="form-input"
        type="search"
        placeholder="Search by name, year, or place…"
        value={query}
        onChange={e => setQuery(e.target.value)}
        style={{ marginBottom: '0.75rem' }}
      />

      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {FILTERS.map(f => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            style={{
              padding: '0.35rem 0.75rem', borderRadius: '999px',
              fontSize: '0.78rem', cursor: 'pointer',
              border: `1px solid ${filter === f.id ? 'var(--green)' : 'var(--border)'}`,
              background: filter === f.id ? 'var(--green)' : 'white',
              color: filter === f.id ? 'white' : 'var(--text)',
            }}
          >
            {f.label} <span style={{ opacity: 0.7 }}>·&nbsp;{counts[f.id]}</span>
          </button>
        ))}
      </div>

      {/* Bulk "request research help" — for owners, on any gap filter (not
          'All'). Posts one investigate_gap quest per filtered person so a
          whole bucket of gaps can be delegated to the marketplace in one go. */}
      {isOwner && filter !== 'all' && rows.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '0.75rem', flexWrap: 'wrap',
          padding: '0.7rem 0.95rem', marginBottom: '1rem',
          background: 'rgba(200,165,110,0.08)', border: '1px solid rgba(200,165,110,0.28)',
          borderRadius: '10px',
        }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
            Stuck on these <strong>{rows.length}</strong>? Post them as mysteries for other researchers to solve.
          </span>
          <button className="btn btn-primary btn-sm" onClick={() => setBulkOpen(true)}>
            🔎 Request research help on {rows.length} →
          </button>
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{
          padding: '2rem', textAlign: 'center',
          background: 'white', border: '1px dashed var(--border)', borderRadius: '10px',
          color: 'var(--text-muted)', fontSize: '0.9rem',
        }}>
          Nothing matches this filter.
        </div>
      ) : (
        <div style={{
          background: 'white', border: '1px solid var(--border)', borderRadius: '10px',
          overflow: 'hidden',
        }}>
          {rows.map((p, i) => {
            const name = [p.preferred_name || p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unknown'
            const year = extractYear(p.birth_date)
            const connCount = connectionCount.get(p.id) || 0
            const isDisconnected = connCount === 0
            const isUnreachable  = focalPersonId && !reachableFromFocal.has(p.id)
            const isMissingDate  = !year
            const isMissingPlace = !p.birth_place
            const cid = clusterIdOf.get(p.id)
            const clusterSize = cid !== undefined ? (clusterSizeOf.get(cid) || 0) : 0
            const isOrphanCluster = cid !== undefined && cid !== mainClusterId && clusterSize >= 2
            const isChainGap = focalPersonId
              && p.id !== focalPersonId
              && reachableFromFocal.has(p.id)
              && !chainReachable.has(p.id)
            const isIncomplete   = isMissingDate || isMissingPlace || isDisconnected || isUnreachable || isOrphanCluster || isChainGap

            const accent = p.gender === 'male' ? '#3b6b5a'
              : p.gender === 'female' ? '#c8a56e'
              : '#9ca3af'

            return (
              <button
                key={p.id}
                onClick={() => onPersonClick && onPersonClick(p.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  width: '100%', padding: '0.75rem 1rem', textAlign: 'left',
                  background: p.id === focalPersonId ? 'color-mix(in srgb, var(--gold) 8%, white)' : 'white',
                  border: 'none', borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  cursor: 'pointer', fontSize: '0.9rem', color: 'var(--text)',
                }}
              >
                <span style={{
                  width: 4, height: 32, background: accent, borderRadius: 2, flexShrink: 0,
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name}
                    </span>
                    {year && (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem', fontWeight: 400 }}>
                        {year}
                      </span>
                    )}
                  </div>
                  {p.birth_place && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: '2px' }}>
                      {p.birth_place}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {filter === 'no_parents' && <Tag color="#7c3aed">needs parents</Tag>}
                  {isMissingDate && <Tag color="#a16207">no date</Tag>}
                  {isMissingPlace && <Tag color="#a16207">no place</Tag>}
                  {isDisconnected && <Tag color="#dc2626">no family</Tag>}
                  {isOrphanCluster && <Tag color="#b45309">cluster of {clusterSize}</Tag>}
                  {isChainGap && <Tag color="#b45309">chain gap</Tag>}
                  {!isDisconnected && !isOrphanCluster && isUnreachable && <Tag color="#6b7280">other branch</Tag>}
                  {!isIncomplete && <Tag color="#15803d">complete</Tag>}
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
    {bulkOpen && (
      <BulkRequestModal
        treeId={currentTree?.id}
        personIds={rows.map(p => p.id)}
        count={rows.length}
        defaultText={FILTER_REQUEST_DEFAULTS[filter]?.text || ''}
        defaultGapType={FILTER_REQUEST_DEFAULTS[filter]?.gap || 'vertical'}
        onClose={() => setBulkOpen(false)}
      />
    )}
    </div>
  )
}

function Tag({ color, children }) {
  return (
    <span style={{
      fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.04em',
      textTransform: 'uppercase', padding: '0.15rem 0.5rem',
      borderRadius: '999px', color,
      background: `color-mix(in srgb, ${color} 12%, white)`,
      border: `1px solid color-mix(in srgb, ${color} 25%, white)`,
    }}>
      {children}
    </span>
  )
}

function buildGraphIndex(persons, relationships, focalPersonId) {
  // connectionCount = parents + partners + children + siblings edges
  const connectionCount = new Map()
  const adj = new Map()

  for (const p of persons) {
    connectionCount.set(p.id, 0)
    adj.set(p.id, [])
  }
  const bump = (a, b) => {
    if (!connectionCount.has(a)) return
    connectionCount.set(a, (connectionCount.get(a) || 0) + 1)
    adj.get(a)?.push(b)
  }

  for (const r of relationships) {
    bump(r.person_a_id, r.person_b_id)
    bump(r.person_b_id, r.person_a_id)
  }

  // BFS from focal to compute reachability
  const reachableFromFocal = new Set()
  if (focalPersonId) {
    const q = [focalPersonId]
    reachableFromFocal.add(focalPersonId)
    while (q.length) {
      const u = q.shift()
      for (const v of adj.get(u) || []) {
        if (reachableFromFocal.has(v)) continue
        reachableFromFocal.add(v)
        q.push(v)
      }
    }
  }

  // Separate adjacency for parent_child only — traverses up through parents
  // AND down through children, in both directions, so everyone genuinely in
  // the ancestor/descendant chain of focal ends up in chainReachable. Anyone
  // reachable via partners/siblings but NOT via parent_child lands in the
  // 'chain gap' bucket: they're kin, but the parent_child edge that would
  // weave them into the tree canvas is missing.
  // hasParent: in a parent_child edge person_a is the parent, person_b the
  // child (matches RelationshipsPanel). So person_b having any such edge means
  // they have at least one known parent. Anyone NOT in this set is missing
  // parents — the prime candidate for a "find the parents" research request.
  const pcAdj = new Map()
  const hasParent = new Set()
  for (const p of persons) pcAdj.set(p.id, [])
  for (const r of relationships) {
    if (r.type !== 'parent_child') continue
    pcAdj.get(r.person_a_id)?.push(r.person_b_id)
    pcAdj.get(r.person_b_id)?.push(r.person_a_id)
    hasParent.add(r.person_b_id)
  }
  const chainReachable = new Set()
  if (focalPersonId && pcAdj.has(focalPersonId)) {
    const q = [focalPersonId]
    chainReachable.add(focalPersonId)
    while (q.length) {
      const u = q.shift()
      for (const v of pcAdj.get(u) || []) {
        if (chainReachable.has(v)) continue
        chainReachable.add(v)
        q.push(v)
      }
    }
  }

  // Connected components (undirected BFS). Each person belongs to exactly one
  // cluster. The "main" cluster is the one containing the focal, or — if no
  // focal is set — the largest cluster. Everything else is an orphan cluster
  // worth surfacing to the user as "probably missing a parent_child link to
  // the main tree".
  const clusterIdOf = new Map()
  const clusterSizeOf = new Map()
  let nextClusterId = 0
  for (const p of persons) {
    if (clusterIdOf.has(p.id)) continue
    const cid = nextClusterId++
    let size = 0
    const queue = [p.id]
    clusterIdOf.set(p.id, cid)
    while (queue.length) {
      const u = queue.shift()
      size++
      for (const v of adj.get(u) || []) {
        if (clusterIdOf.has(v)) continue
        clusterIdOf.set(v, cid)
        queue.push(v)
      }
    }
    clusterSizeOf.set(cid, size)
  }

  let mainClusterId = focalPersonId ? clusterIdOf.get(focalPersonId) : undefined
  if (mainClusterId === undefined) {
    // Fall back to the largest cluster.
    let best = -1
    for (const [cid, size] of clusterSizeOf) {
      if (size > best) { best = size; mainClusterId = cid }
    }
  }

  return { connectionCount, reachableFromFocal, clusterIdOf, clusterSizeOf, mainClusterId, chainReachable, hasParent }
}
